import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  analyzeMvx,
  indexBoundaryLabels,
  indexSegments,
  MvxBarSegment,
  MvxIndexResult,
  MvxInputs,
  MvxPattern,
  MvxPatternId,
  MvxTriageEntry,
  MvxFactorResult,
  triageReason,
  MvxZone,
  Specimen,
} from '../../models/MetabolicVulnerability';
import { MvxGaugeBand, MvxScoreGauge } from '../charts/MvxScoreGauge';
import { targetGradations } from '../charts/MvxSeverityBar';
import { SeverityBand, severityRampColor } from '../charts/chartPalette';

/**
 * Metabolic Vulnerability (MVX Plus) Insights panel. The interpretation layer:
 * the hero MVX gauge + a plain-language read of the score and the patterns it
 * points to. The marker bar graphs live in the sibling Results panel
 * (`MvxResultsPanel`); the step routing lives in `MvxBlueprintPanel`.
 *
 * Takes an `MvxInputs` bag off the bloodwork (markers aren't in `Bloodwork` yet).
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  specimen?: Specimen;
}

/** Pull the nine MVX values off a bloodwork into the model's input shape. */
function mvxInputsOf(bw: any): MvxInputs {
  const src = bw || {};
  return {
    mvx: src.mvx, ivx: src.ivx, mmx: src.mmx,
    glycA: src.glycA, sHdlP: src.sHdlP, citrate: src.citrate,
    valine: src.valine, leucine: src.leucine, isoleucine: src.isoleucine,
  };
}

export function inputsFromBloodwork(ba?: BloodworkAnalysis): MvxInputs {
  return mvxInputsOf(ba && ba.bloodwork ? ba.bloodwork : {});
}

/**
 * The same nine fields off the PREVIOUS test, so the Results bars can draw the
 * blue comparison drop beside the black current one (the idiom every other
 * reference-range chart uses). `previousBloodwork` is set by bloodwork-display
 * from the chosen comparison test, or the next test down the list; it is
 * undefined on a client's first test, and then every bar draws one drop.
 */
export function previousInputsFromBloodwork(ba?: BloodworkAnalysis): MvxInputs {
  return mvxInputsOf(ba ? (ba as any).previousBloodwork : undefined);
}

/**
 * How many arcs the gauge's ramp is painted with, across the whole scale.
 *
 * The arc cannot take the bars' gradient directly: a LINEAR gradient laid
 * across a circle reads as a lighting error, lightest on one side, rather
 * than as a scale. So the gauge samples the same stops into a run of thin
 * flat arcs, which gives the shading the one thing a linear gradient cannot,
 * a direction that follows the scale around the curve.
 *
 * ~96 across a 260px-wide semicircle is about 3px an arc, under the width at
 * which the steps read as steps.
 */
const GAUGE_RAMP_STEPS = 96;

/**
 * The gauge's arc, shaded exactly like the bar under it (Maryann, 2026-10-16).
 *
 * The four gauge segments ARE the four bar bands in the same order, so each
 * one samples severityRampColor at its own band index: deep green at the
 * floor easing to green at the target, yellow through the Borderline tick,
 * orange arriving at the lab limit, red just past it, deepening to the top of
 * the scale. The numbers all live in chartPalette, so a change to the shading
 * lands on the gauge and the bars together.
 *
 * ⚠ DISPLAY ONLY, like the bars: indexZone still reads the hard band edges,
 * and the word under the needle still comes from it.
 */
const rampPlotBands = (segs: MvxBarSegment[], softenFrom?: number): MvxGaugeBand[] => {
  const span = segs[segs.length - 1].to - segs[0].from;
  const out: MvxGaugeBand[] = [];
  if (!(span > 0)) return out;
  segs.forEach((seg, band) => {
    const width = seg.to - seg.from;
    if (!(width > 0)) return;
    const steps = Math.max(1, Math.round(GAUGE_RAMP_STEPS * (width / span)));
    const step = width / steps;
    // Where the target sits inside Optimal, at its TRUE proportion of the
    // band: the arc is linear in score space, where the bar deliberately
    // bends its green band to pin the target at a fixed spot.
    const targetFrac =
      band === 0 && softenFrom !== undefined && softenFrom > seg.from && softenFrom < seg.to
        ? (softenFrom - seg.from) / width
        : undefined;
    for (let k = 0; k < steps; k++) {
      const from = seg.from + step * k;
      out.push({
        // Sampled across the CLOSED interval, so the first arc of the scale is
        // exactly the ramp's deep green and the last is exactly its deep red,
        // the same two colors that end the bars. (Midpoints would land a half
        // step inside and never paint either end.) The cost is that each arc
        // carries the color of a point up to one arc-width, about a pixel,
        // ahead of where it is drawn.
        color: severityRampColor(band as SeverityBand, steps > 1 ? k / (steps - 1) : 0.5, targetFrac),
        from,
        // Half a step of overlap: arcs that abut exactly leave a hairline
        // antialiasing seam, and whatever is drawn next covers the overhang.
        to: Math.min(from + step * 1.5, segs[segs.length - 1].to),
      });
    }
  });
  return out;
};
const ZONE_LABEL: Record<MvxZone, string> = { green: 'Optimal', yellow: 'Borderline', orange: 'Elevated', red: 'High' };

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

// Capitalize every word (incl. after hyphens) for the pattern bullets.
const titleCase = (s: string): string =>
  s.toLowerCase().replace(/(^|\s|-)(\w)/g, (_m, sep, c) => sep + c.toUpperCase());

/**
 * Darren Schmidt's "start here" line. The ranking is the model's
 * (`resolveMvxTriage`); this only words it. His rule in one sentence: work the
 * lab-abnormal marker that is furthest out, and when nothing is lab-abnormal
 * work whichever is furthest from target — there is always a next one.
 *
 * Deliberately names a MARKER, never a score: "There is no 'treating' the MVX,
 * IVX. They are a guide" (Schmidt, 2026-09-20), which is why the model ranks
 * only the six measured factors.
 */
/**
 * IT STILL NAMES ONE WHEN EVERYTHING IS AT TARGET, because he does.
 *
 * Slide 70 of his deck: "If all numbers are excellent, still find the one
 * 'most out of range'. In this example, GlycA is the highest. He eats sweets
 * and his teeth get infected." The pick our model makes in that case is
 * exactly his: the marker furthest from its own ideal point, which on a
 * lower-better marker is simply the highest.
 *
 * What was wrong was the WORDS. The line used to read "closest to leaving its
 * target range", which described a distance to the band edge that nothing was
 * measuring, and on a GlycA of 220 against a target of 260 was not true
 * anyway (Maryann, 2026-10-09). The wording now follows his example and
 * depends on the marker's direction; see AT_TARGET_REASON.
 */
const startHere = (triage: MvxTriageEntry[], factors: MvxFactorResult[]): React.ReactNode => {
  const first = triage[0];
  if (!first) return null;
  /**
   * Read off the FACTOR STATES, not off the leading tier.
   *
   * `first.tier === 'atTarget'` used to stand in for "everything is at
   * target", and that broke the moment triage could leave a marker out by
   * direction (see `triageSide`): a Small HDL-P of 23 is skipped, every other
   * factor is at target, and the panel would have announced that all measured
   * factors were at target while the bar beside it drew that 23 outside its
   * green. The states cannot disagree with the bars, so ask them.
   */
  const allAtTarget = factors.filter(f => f.hasValue).every(f => f.state === 'opt');
  // Only markers that still need work queue up behind the first one. A marker
  // sitting at target is not "next", it is done — listing it would read as a
  // to-do. So the Then line disappears once everything else is on target.
  const next = triage.filter((e, i) => i > 0 && e.tier !== 'atTarget').slice(0, 2);
  return (
    <div style={{ borderTop: '1px solid #e4dfd8', marginTop: 10, paddingTop: 8 }}>
      {allAtTarget && (
        <div style={{ marginBottom: 4 }}>All measured factors are at target.</div>
      )}
      <div style={{ fontWeight: 'bold' }}>Start with:</div>
      <div>
        <span style={{ fontWeight: 'bold' }}>{first.short}</span>
        {` — ${triageReason(first)}.`}
      </div>
      {next.length > 0 && (
        <div style={{ marginTop: 4, color: '#8a837b' }}>{`Then: ${next.map(e => e.short).join(', ')}`}</div>
      )}
    </div>
  );
};

/**
 * Client-facing read under the gauge. Leads with the overall MVX score, names
 * the driving sub-score(s) — inflammation (IVX) vs metabolic strain (MMX) — then
 * lists the specific patterns as bullets. Adapts to whichever patterns fire.
 */
const interpretation = (mvx: MvxIndexResult, patterns: MvxPattern[], hasFactors: boolean): React.ReactNode => {
  if (!hasFactors) {
    return 'An overall metabolic-resilience score. Add the six MVX Plus factor values to surface the patterns and Blueprint steps.';
  }
  const level = ZONE_LABEL[mvx.zone].toLowerCase();
  const scoreText = mvx.hasValue ? ` (${fmt(mvx.value)})` : '';
  const active = patterns.filter(p => p.id !== MvxPatternId.Optimal);
  if (active.length === 0) {
    return `The MVX score is ${level}${scoreText} — markers sit within their optimal ranges.`;
  }
  const ivxDriven = active.some(p => p.id === MvxPatternId.Inflammation || p.id === MvxPatternId.ToxicBurden);
  const mmxDriven = active.some(
    p => p.id === MvxPatternId.Mitochondrial || p.id === MvxPatternId.Malnutrition || p.id === MvxPatternId.CarbLoad,
  );
  let driver = '';
  if (ivxDriven && mmxDriven) driver = ', driven by inflammation (IVX) and metabolic strain (MMX)';
  else if (ivxDriven) driver = ', driven by inflammation (IVX)';
  else if (mmxDriven) driver = ', driven by metabolic strain (MMX)';
  return (
    <>
      <div style={{ fontWeight: 'bold' }}>{`The MVX score is ${level}${scoreText}${driver}. The markers show:`}</div>
      <ul style={{ listStyle: 'none', paddingLeft: 0, marginLeft: 0, marginTop: 4, marginBottom: 0 }}>
        {active.map((p, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', color: '#337ab7', fontWeight: 'bold', marginBottom: 2 }}>
            <span style={{ display: 'inline-block', marginRight: 4 }}>•</span>
            <span>{titleCase(p.clientTitle)}</span>
          </li>
        ))}
      </ul>
    </>
  );
};

export class MetabolicVulnerabilityInsights extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    return analyzeMvx(inputsFromBloodwork(ba)).anyEntered;
  }

  // MVX hero gauge — the same Highcharts solid-gauge as the Glucose gauge, with
  // the zone bands from the (specimen-specific) decision limit. The black
  // "Metabolic Vulnerability is" line + blue "{level} ({score})" render inside it.
  renderHero(mvx: MvxIndexResult) {
    if (!mvx.hasValue) {
      return (
        <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: '18px 0' }}>
          Enter the MVX score to show the vulnerability gauge.
        </div>
      );
    }
    // The MVX score can exceed 100 — scale the gauge (and its red band) up past
    // 100 so the needle shows the true value instead of pegging at the edge.
    const gaugeMax = Math.max(100, Math.ceil(mvx.value / 10) * 10);
    const segs = indexSegments(mvx, this.props.specimen, gaugeMax);
    const plotBands = rampPlotBands(segs, mvx.softenFrom);
    // THE SAME RULER AS THE BAR (Maryann, 2026-10-16). Three things on it:
    //
    //   · the floor and the three zone boundaries (green/yellow · decision
    //     limit · orange/red), each printing the LAST score inside the band
    //     below it (39 · 62 · 77) rather than the first one outside it — see
    //     indexBoundaryLabels. Not the top endpoint: the scale can run past
    //     100, so the top stays unlabeled.
    //   · the target.
    //   · ONE mark on the run up to it: the middle of the bar's three
    //     gradations, which is the halfway point between the floor and the
    //     target. It comes out of targetGradations so the number is one the
    //     bar prints too, rather than a second opinion about where halfway is.
    //
    // All three of the bar's gradations was the first try. Eight marks fit on
    // a ~360px arc, but five of them land in the lower-left quadrant, since
    // that is where the range a practitioner works in lives, and it read as a
    // crowd (Maryann, 2026-10-16: "let's remove 8.5 and 25.5"). The bar has
    // the room for a full ruler, the arc has room for one waypoint.
    const gradations = mvx.softenFrom === undefined ? [] : targetGradations(segs[0].from, mvx.softenFrom);
    const ticks = Array.from(
      new Set([
        ...indexBoundaryLabels(segs).slice(0, 4),
        ...(mvx.softenFrom === undefined
          ? []
          : [gradations[(gradations.length - 1) / 2 | 0], mvx.softenFrom]),
      ]),
    ).sort((a, b) => a - b);
    return (
      <MvxScoreGauge
        value={mvx.value}
        text="Metabolic Vulnerability is"
        category={ZONE_LABEL[mvx.zone]}
        plotBands={plotBands}
        ticks={ticks}
        max={gaugeMax}
        chartContainerId="mvxVulnerabilityGauge"
      />
    );
  }

  // The two sub-scores (IVX / MMX) explained, under the MVX gauge.
  renderSubScores(indices: MvxIndexResult[]) {
    const subs = indices.filter(i => i.id !== 'mvx');
    return (
      <div style={{ marginTop: 12, padding: '10px 8px 0', borderTop: '1px solid #eee7df', textAlign: 'left' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#2a2a2a', marginBottom: 8 }}>
          The MVX score combines two sub-scores:
        </div>
        {subs.map(ix => (
          <div key={ix.id} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#2a2a2a' }}>
              {ix.short} · {ix.label}
            </div>
            <div style={{ fontSize: 10.5, color: '#857d73', lineHeight: 1.4, marginTop: 1 }}>{ix.client}</div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * The panel's content as data, for the AI export. Same shape the other
   * panels' exportInsights() return: what the practitioner sees, nothing more.
   *
   * ⚠ PRACTITIONER DOWNLOAD ONLY. The caller gates this behind `includeMvx`,
   * which only the raw-data download sets. It must not reach the client-facing
   * Overall Progress & Trends draft while MVX is unreleased and its clinical
   * questions are open with Dr. Schmidt — an AI summary gets read as a
   * finding, and this section still contains an IVX edge we chose ourselves
   * and a citrate low side with no published support. Same reasoning that
   * holds Thyroid, Hydration and Iron out of the export entirely.
   */
  static exportInsights(bloodworkAnalysis: BloodworkAnalysis): {
    indices: Array<{ id: string; short: string; label: string; value: number; zone: string }>;
    factors: Array<{ id: string; short: string; value: number; units: string; state: string }>;
    patterns: Array<{ categoryId: number; practitionerName: string; markerRow: string }>;
    startWith?: { marker: string; reason: string };
  } | null {
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis));
    if (!result.anyEntered) return null;

    const top = result.triage[0];
    return {
      indices: result.indices
        .filter(i => i.hasValue)
        .map(i => ({ id: i.id, short: i.short, label: i.label, value: i.value, zone: i.zone })),
      factors: result.factors
        .filter(f => f.hasValue)
        .map(f => ({ id: f.id, short: f.short, value: f.value, units: f.units, state: f.state })),
      patterns: result.patterns.map(p => ({
        categoryId: p.id,
        practitionerName: p.practitionerName,
        markerRow: p.markerRow,
      })),
      ...(top ? { startWith: { marker: top.short, reason: top.tier } } : {}),
    };
  }

  render() {
    const { bloodworkAnalysis, specimen } = this.props;
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis), specimen);
    if (!result.anyEntered) return null;

    const mvx = result.indices.find(i => i.id === 'mvx')!;
    const hasFactors = result.factors.some(f => f.hasValue);

    return (
      <Card className="evaluationPanel" id="metabolic-vulnerability-insights">
        <Card.Header className="evaluationPanelHeading">
          <div>MVX Plus Insights</div>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {this.renderHero(mvx)}
            <div style={{ fontSize: 12, color: '#4a443e', lineHeight: 1.5, padding: '10px 8px 0', textAlign: 'left' }}>
              {interpretation(mvx, result.patterns, hasFactors)}
              {hasFactors && startHere(result.triage, result.factors)}
            </div>
            {this.renderSubScores(result.indices)}
            <div style={{ textAlign: 'center', fontSize: 10.5, color: '#999', marginTop: 10 }}>
              A lower score is better.
              <br />
              Observational — not diagnostic.
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default MetabolicVulnerabilityInsights;
